import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  Link,
} from '@mui/material';

const sanitize = (raw) => {
  const upper = (raw || '').toUpperCase().replace(/[^A-HJ-NP-Z2-9]/g, '');
  return upper.slice(0, 8);
};

const display = (raw) => {
  const c = sanitize(raw);
  if (c.length <= 4) return c;
  return `${c.slice(0, 4)}-${c.slice(4)}`;
};

// ─── small UI helpers ────────────────────────────────────────────────────────

const CodeBlock = ({ children, label }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };
  return (
    <Box
      sx={{
        position: 'relative',
        my: 1.5,
        bgcolor: 'grey.900',
        color: 'grey.100',
        p: 2,
        pr: 6,
        borderRadius: 1,
        fontFamily: 'monospace',
        fontSize: '0.85rem',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
      aria-label={label || 'code block'}
    >
      {children}
      <Tooltip title={copied ? 'Copié !' : 'Copier'}>
        <IconButton
          size="small"
          onClick={handleCopy}
          sx={{ position: 'absolute', top: 6, right: 6, color: 'grey.300' }}
          aria-label="copier le code"
        >
          {copied ? '✓' : '⧉'}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const Section = ({ title, children, sx }) => (
  <Box sx={{ mb: 4, ...sx }}>
    <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

// ─── main page ──────────────────────────────────────────────────────────────

export default function CliPage() {
  const [searchParams] = useSearchParams();
  const user = useSelector((s) => s?.auth?.user || {});
  const tokenFromState = useSelector((s) => s?.auth?.token);
  const userID = user.userID || user.id || '';
  const isAuthenticated = useSelector((s) => s?.auth?.isAuthenticated) || !!userID;

  // form state
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [error, setError] = useState(null);
  const [response, setResponse] = useState(null);

  // pre-fill from ?code= query param + scroll to form
  const authSectionRef = useRef(null);
  useEffect(() => {
    const fromUrl = searchParams.get('code');
    if (fromUrl) {
      setCode(sanitize(fromUrl));
      authSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [searchParams]);

  const codeReady = useMemo(() => sanitize(code).length === 8, [code]);

  if (!isAuthenticated) {
    const redirect = encodeURIComponent(`/adminpage/cli?code=${sanitize(code)}`);
    return <Navigate to={`/login?from=${redirect}`} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setError(null);
    try {
      const headers = {};
      if (tokenFromState && !axios.defaults.headers.common['Authorization']) {
        headers.Authorization = `Bearer ${tokenFromState}`;
      }
      if (userID && !axios.defaults.headers.common['UserID']) {
        headers.UserID = userID;
      }
      const res = await axios.post(
        '/cli/auth/approve',
        {
          user_code: sanitize(code),
          user_id: userID,
          label: label || undefined,
        },
        { headers },
      );
      setResponse(res.data);
      setStatus('success');
    } catch (err) {
      const data = err?.response?.data || {};
      setError({
        code: data.error || 'network_error',
        message: data.error_description || err.message || 'Erreur réseau',
      });
      setStatus('error');
    }
  };

  return (
    <Box sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ maxWidth: 820, mx: 'auto', p: { xs: 3, sm: 5 } }}>
        <Typography variant="h4" gutterBottom>
          CLI MyJouleBox
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Interagir avec MyJouleBox depuis votre terminal (et depuis un agent IA
          via MCP). Cette page combine la validation d'un code d'autorisation et
          le guide d'installation.
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          La CLI est <strong>en lecture seule</strong>. Aucune modification
          serveur n'est possible.
        </Alert>

        <Divider sx={{ mb: 3 }} />

        {/* ─── Authorize a CLI (primary action) ──────────────── */}
        <Section title="Autoriser une CLI" sx={{ scrollMarginTop: 80 }}>
          <Box ref={authSectionRef} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Entrez le code à 8 caractères affiché dans votre terminal après{' '}
            <code>mjb login</code>.
          </Typography>

          {status === 'success' && response ? (
            <Stack spacing={2}>
              <Alert severity="success">CLI autorisée avec succès.</Alert>
              <Typography variant="body2">
                Vous pouvez retourner dans votre terminal.
              </Typography>
              {response.hostname && (
                <Typography variant="caption" color="text.secondary">
                  Machine déclarée : <code>{response.hostname}</code>
                </Typography>
              )}
              {response.expires_at && (
                <Typography variant="caption" color="text.secondary">
                  Token expire le{' '}
                  {new Date(response.expires_at).toLocaleDateString()}
                </Typography>
              )}
            </Stack>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <Stack spacing={2}>
                <TextField
                  label="Code"
                  placeholder="XXXX-XXXX"
                  value={display(code)}
                  onChange={(e) => setCode(sanitize(e.target.value))}
                  inputProps={{
                    autoCapitalize: 'characters',
                    autoCorrect: 'off',
                    spellCheck: false,
                    style: {
                      fontFamily: 'monospace',
                      fontSize: '1.4rem',
                      letterSpacing: '0.15em',
                      textAlign: 'center',
                    },
                  }}
                  fullWidth
                  autoFocus
                  error={!!code && !codeReady}
                  helperText={
                    !!code && !codeReady
                      ? '8 caractères requis'
                      : "Format : 8 caractères, sans I/L/O/0/1"
                  }
                />
                <TextField
                  label="Nom de la machine (optionnel)"
                  placeholder="laptop-perso"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  fullWidth
                />
                {status === 'error' && error && (
                  <Alert severity="error">
                    <strong>{error.code}</strong> — {error.message}
                  </Alert>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={!codeReady || status === 'loading'}
                  size="large"
                  startIcon={
                    status === 'loading' ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : null
                  }
                >
                  {status === 'loading' ? 'Validation…' : 'Autoriser la CLI'}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Connecté en tant que <strong>{user.email || userID}</strong>.
                  La CLI aura les mêmes permissions que votre compte.
                </Typography>
              </Stack>
            </form>
          )}
        </Section>

        <Divider sx={{ mb: 4 }} />

        {/* ─── Step 1: install (one-liner) ─────────────────── */}
        <Section title="1. Installer le binaire">
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>macOS / Linux</strong> — colle ça dans un terminal :
          </Typography>
          <CodeBlock label="install macOS / Linux">{`curl -fsSL ${window.location.origin}/cli-install.sh | sh`}</CodeBlock>

          <Typography variant="body2" sx={{ mb: 1, mt: 2 }}>
            <strong>Windows</strong> — colle ça dans PowerShell :
          </Typography>
          <CodeBlock label="install Windows">{`iwr -useb ${window.location.origin}/cli-install.ps1 | iex`}</CodeBlock>

          <Typography variant="body2" sx={{ mt: 2 }}>
            Le script détecte ton OS+arch, télécharge le binaire depuis la
            dernière release et le pose dans ton <code>PATH</code>. Variables
            d'env honorées : <code>MJB_VERSION</code>, <code>INSTALL_DIR</code>.
          </Typography>

        </Section>

        {/* ─── Step 2: login ───────────────────────────────── */}
        <Section title="2. Se connecter (one-shot avec `mjb setup`)">
          <Typography variant="body2" sx={{ mb: 1 }}>
            La commande <code>mjb setup</code> orchestre login + sélection de
            compte + enregistrement du serveur MCP pour Claude Code en une
            seule fois :
          </Typography>
          <CodeBlock>{`mjb setup`}</CodeBlock>
          <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
            Ou les commandes individuelles : <code>mjb login</code>, puis
            <code> mjb accounts use &lt;id&gt;</code>, puis
            <code> mjb install-mcp</code>. Le token est valable 30 jours.
          </Typography>
          <CodeBlock>{`$ mjb login

Pour autoriser cette CLI :
  1. Ouvre ${window.location.origin}/adminpage/cli
  2. Entre ce code : K7P4-N2QV

(En attente — expire dans 600 s)
✓ Connecté. Token expire le 2026-06-17`}</CodeBlock>
          <Typography variant="body2">
            Le token est stocké dans <code>~/.config/mjb-cli/credentials.json</code>{' '}
            (Linux/macOS) ou <code>%APPDATA%\mjb-cli\credentials.json</code>{' '}
            (Windows), avec permissions <code>0600</code> sur Unix.
          </Typography>
        </Section>

        {/* ─── Step 4: multi-account ───────────────────────── */}
        <Section title="3. Gérer plusieurs comptes">
          <Typography variant="body2" sx={{ mb: 1 }}>
            Si votre utilisateur est rattaché à plusieurs organisations :
          </Typography>
          <CodeBlock>{`mjb accounts list           # liste vos comptes
mjb accounts use <id>       # fige un compte courant
mjb --account <id> whoami   # override ponctuel`}</CodeBlock>
        </Section>

        {/* ─── Step 5: verify ──────────────────────────────── */}
        <Section title="4. Vérifier">
          <CodeBlock>{`$ mjb whoami
Utilisateur : panagiotis.pentovelis@myjoulebox.com
UserID      : <uuid>
Compte      : <uuid>
Émis le     : 2026-05-18 22:30
Expire le   : 2026-06-17 22:30

$ mjb ping
✓ pong — API joignable et token valide`}</CodeBlock>
        </Section>

        {/* ─── Step 6: MCP integration ─────────────────────── */}
        <Section title="5. Exposer la CLI à un agent IA (MCP)">
          <Typography variant="body2" sx={{ mb: 1 }}>
            La CLI peut tourner comme serveur{' '}
            <Link
              href="https://modelcontextprotocol.io"
              target="_blank"
              rel="noreferrer"
              underline="hover"
            >
              MCP
            </Link>{' '}
            (<code>mjb mcp serve</code>) — chaque commande devient un tool
            utilisable par Claude Code, Cursor, etc.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Claude Code</strong> — une seule commande l'enregistre
            automatiquement dans <code>~/.claude.json</code> :
          </Typography>
          <CodeBlock label="install MCP">{`mjb install-mcp`}</CodeBlock>
          <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
            Idempotent (sûr à relancer). Options :{' '}
            <code>mjb install-mcp --print</code> pour prévisualiser sans
            écrire, <code>mjb install-mcp --remove</code> pour le retirer.
            Redémarre Claude Code ensuite, puis vérifie avec{' '}
            <code>/mcp</code>.
          </Typography>
          <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
            Ou manuellement, en ajoutant à <code>~/.claude.json</code> :
          </Typography>
          <CodeBlock label="config Claude Code">{`{
  "mcpServers": {
    "mjb": {
      "command": "mjb",
      "args": ["mcp", "serve"]
    }
  }
}`}</CodeBlock>
          <Alert severity="warning" sx={{ mt: 2 }}>
            La CLI doit être loggée (<code>mjb login</code>) <em>avant</em> de
            lancer l'agent. L'agent hérite des permissions de ton compte.
          </Alert>
        </Section>

        {/* ─── Step 7: commands cheatsheet ─────────────────── */}
        <Section title="6. Aide-mémoire des commandes">
          <CodeBlock>{`mjb whoami                        # session courante
mjb ping                          # vérifier l'API
mjb accounts list                 # tous mes comptes
mjb accounts use <id>             # changer de compte
mjb logout                        # déconnexion + révocation

# Commandes business (à venir, lecture seule) :
mjb clients list
mjb boxes list
mjb payments history
mjb subscriptions list
mjb stock products
mjb transfers list <nature>`}</CodeBlock>
        </Section>

        {/* ─── Step 8: troubleshooting ─────────────────────── */}
        <Section title="7. Dépannage">
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                <code>401 — Token expired</code>
              </Typography>
              <Typography variant="body2">
                Token de 30 jours expiré : relance <code>mjb login</code>.
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                <code>code expiré</code>
              </Typography>
              <Typography variant="body2">
                Le code device dure 10 min. Relance <code>mjb login</code>.
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                <code>429 — slow_down</code>
              </Typography>
              <Typography variant="body2">
                Rate-limit serveur : attends 1 minute avant de retenter.
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                <code>account_forbidden</code>
              </Typography>
              <Typography variant="body2">
                Le compte choisi n'est plus accessible : <code>mjb accounts list</code>{' '}
                pour voir les comptes valides.
              </Typography>
            </Box>
          </Stack>
        </Section>

        <Divider sx={{ my: 3 }} />
        <Typography variant="caption" color="text.secondary">
          Problème non couvert ? Ouvre un ticket avec <code>mjb --version</code>{' '}
          et la sortie de la commande qui a échoué.
        </Typography>
      </Paper>
    </Box>
  );
}
